import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import { aiApi } from "../api/aiApi";
import { clientsApi } from "../api/clientsApi";
import { disputesApi, type UpdateDisputeRequest } from "../api/disputesApi";
import { disputeLettersApi } from "../api/disputeLettersApi";
import { Client, DisputeItem, DisputeLetter, LetterTemplate } from "../types";

type BoardColumn = "in dispute" | "awaiting response" | "updated" | "deleted/resolved";

interface DisputeCard extends DisputeItem {
  clientName: string;
}

const boardColumns: { title: string; key: BoardColumn }[] = [
  { title: "In Dispute", key: "in dispute" },
  { title: "Awaiting Response", key: "awaiting response" },
  { title: "Updated", key: "updated" },
  { title: "Deleted/Resolved", key: "deleted/resolved" },
];

const DISPUTE_ACCOUNT_TYPES = ["Collection", "Late Payment", "Charge-off", "Inquiry", "Other"] as const;
const DISPUTE_STATUSES = ["In Dispute", "Awaiting Response", "Updated", "Deleted/Resolved"] as const;
const LETTER_BUREAU_TARGETS = ["Equifax", "Experian", "TransUnion", "Creditor / Furnisher"] as const;

const FALLBACK_LETTER_TEMPLATES: LetterTemplate[] = [
  { typeKey: "Validation", name: "Validation", description: "", guidance: "" },
  { typeKey: "Investigation", name: "Investigation", description: "", guidance: "" },
  { typeKey: "Goodwill", name: "Goodwill", description: "", guidance: "" },
  { typeKey: "Other", name: "Other", description: "", guidance: "" },
];

const modalInputClass =
  "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500/40 focus:ring-2";
const modalCardClass =
  "rounded-xl border border-slate-800 bg-slate-900/95 p-4 shadow-lg shadow-black/20 sm:p-6";

function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso?.trim()) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalValueToIso(local: string): string | null {
  if (!local.trim()) {
    return null;
  }
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const typeColorMap: Record<string, string> = {
  collection: "border-red-500/40 bg-red-500/10",
  "late payment": "border-orange-500/40 bg-orange-500/10",
  "charge-off": "border-purple-500/40 bg-purple-500/10",
  inquiry: "border-blue-500/40 bg-blue-500/10",
};

function normalizeColumn(status: string): BoardColumn {
  const s = status.toLowerCase();
  if (s.includes("await")) return "awaiting response";
  if (s.includes("update")) return "updated";
  if (s.includes("resolved") || s.includes("delete")) return "deleted/resolved";
  return "in dispute";
}

function getBureaus(dispute: DisputeItem): string[] {
  return [
    dispute.equifaxDisputed ? "Equifax" : "",
    dispute.experianDisputed ? "Experian" : "",
    dispute.transUnionDisputed ? "TransUnion" : "",
  ].filter(Boolean);
}

function typeClass(accountType: string): string {
  return typeColorMap[accountType.toLowerCase()] ?? "border-slate-700 bg-slate-900";
}

const DisputesPage: React.FC = () => {
  const [cards, setCards] = useState<DisputeCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<DisputeCard | null>(null);
  const [detailDraft, setDetailDraft] = useState<DisputeItem | null>(null);
  const [detailDisputeId, setDetailDisputeId] = useState<string | null>(null);
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [detailLetters, setDetailLetters] = useState<DisputeLetter[]>([]);
  const [detailLettersLoading, setDetailLettersLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailSaveError, setDetailSaveError] = useState("");
  const [detailLetterError, setDetailLetterError] = useState("");
  const [generatingDetailLetter, setGeneratingDetailLetter] = useState(false);
  const [savingDetailLetter, setSavingDetailLetter] = useState(false);
  const [generatedLetterBody, setGeneratedLetterBody] = useState("");
  const [letterGenLetterType, setLetterGenLetterType] = useState("");
  const [letterGenBureau, setLetterGenBureau] = useState<string>(LETTER_BUREAU_TARGETS[0]);
  const [expandedHistoryLetterId, setExpandedHistoryLetterId] = useState<string | null>(null);
  const [generatingDisputeReason, setGeneratingDisputeReason] = useState(false);

  const [bureauFilter, setBureauFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const clients = await clientsApi.getAll();
      const disputesByClient = await Promise.all(
        clients.map(async (client: Client) => {
          const disputes = await disputesApi.getAllForClient(client.id);
          return disputes.map((d) => ({
            ...d,
            clientName: `${client.firstName} ${client.lastName}`,
          }));
        })
      );
      setCards(disputesByClient.flat());
    } catch {
      setError("Failed to load disputes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const disputeLetterOptions = useMemo(
    () => (letterTemplates.length > 0 ? letterTemplates : FALLBACK_LETTER_TEMPLATES),
    [letterTemplates]
  );

  const closeDetailModal = useCallback(() => {
    setSelected(null);
    setDetailDraft(null);
    setDetailDisputeId(null);
    setDetailSaveError("");
    setDetailLetterError("");
    setGeneratedLetterBody("");
    setExpandedHistoryLetterId(null);
    setDetailLetters([]);
    setGeneratingDisputeReason(false);
  }, []);

  const openDetailModal = useCallback(
    (card: DisputeCard) => {
      const options = disputeLetterOptions;
      const typeKey =
        options.find((t) => t.typeKey === card.letterType || t.name === card.letterType)?.typeKey ??
        (card.letterType || options[0]?.typeKey || "");
      setSelected(card);
      setDetailDisputeId(card.id);
      setDetailDraft({ ...card, letterType: typeKey });
      setDetailSaveError("");
      setDetailLetterError("");
      setGeneratedLetterBody("");
      setLetterGenLetterType(typeKey || options[0]?.typeKey || "");
      setLetterGenBureau(LETTER_BUREAU_TARGETS[0]);
      setExpandedHistoryLetterId(null);
    },
    [disputeLetterOptions]
  );

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    void disputeLettersApi.getTemplates().then(setLetterTemplates).catch(() => setLetterTemplates([]));
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      setDetailLetters([]);
      setDetailLettersLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLettersLoading(true);
    void disputeLettersApi
      .getLettersForDispute(detailDisputeId)
      .then((list) => {
        if (!cancelled) {
          setDetailLetters(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetailLetters([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLettersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDetailModal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailDisputeId, closeDetailModal]);

  const generateDisputeReasonWithAi = async () => {
    if (!detailDraft) {
      return;
    }
    const creditor = detailDraft.creditor.trim();
    if (!creditor || !detailDraft.accountType) {
      return;
    }
    setGeneratingDisputeReason(true);
    setDetailSaveError("");
    try {
      const balanceParsed = detailDraft.balance;
      const bureausParts: string[] = [];
      if (detailDraft.equifaxDisputed) {
        bureausParts.push("Equifax");
      }
      if (detailDraft.experianDisputed) {
        bureausParts.push("Experian");
      }
      if (detailDraft.transUnionDisputed) {
        bureausParts.push("TransUnion");
      }
      const bureaus = bureausParts.length > 0 ? bureausParts.join(", ") : null;

      const result = await aiApi.generateDisputeReason({
        creditorName: creditor,
        accountType: detailDraft.accountType,
        accountNumber: detailDraft.accountNumber.trim() || undefined,
        balance: Number.isNaN(balanceParsed) ? null : balanceParsed,
        status: detailDraft.status,
        bureaus,
        additionalContext: detailDraft.notes.trim() || null,
      });
      setDetailDraft((prev) => (prev ? { ...prev, disputeReason: result.reason } : null));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not generate dispute reason.";
      setDetailSaveError(message);
    } finally {
      setGeneratingDisputeReason(false);
    }
  };

  const submitDetailDisputeSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailDraft || !selected) {
      return;
    }
    const balanceNum = detailDraft.balance;
    if (Number.isNaN(balanceNum)) {
      setDetailSaveError("Enter a valid balance.");
      return;
    }
    setDetailSaving(true);
    setDetailSaveError("");
    try {
      const d = detailDraft;
      const now = new Date().toISOString();
      const payload: UpdateDisputeRequest = {
        creditor: d.creditor.trim(),
        accountType: d.accountType,
        accountNumber: d.accountNumber.trim(),
        balance: balanceNum,
        status: d.status,
        disputeReason: d.disputeReason.trim(),
        disputeStrategy: d.disputeStrategy,
        letterType: d.letterType.trim(),
        priorityRank: d.priorityRank,
        estimatedScoreImpact: d.estimatedScoreImpact,
        equifaxDisputed: d.equifaxDisputed,
        experianDisputed: d.experianDisputed,
        transUnionDisputed: d.transUnionDisputed,
        roundNumber: d.roundNumber,
        statuteOfLimitationsFlag: d.statuteOfLimitationsFlag,
        notes: d.notes.trim(),
        lastLetterSentAt: d.lastLetterSentAt ?? null,
        responseReceivedAt: d.responseReceivedAt ?? null,
        updatedAt: now,
      };
      const updated = await disputesApi.update(d.clientId, d.id, payload);
      setDetailDraft(updated);
      setSelected({ ...updated, clientName: selected.clientName });
      await loadData();
    } catch {
      setDetailSaveError("Could not save dispute.");
    } finally {
      setDetailSaving(false);
    }
  };

  const handleGenerateDetailLetter = async () => {
    if (!detailDisputeId || !letterGenLetterType) {
      setDetailLetterError("Select a letter type.");
      return;
    }
    setDetailLetterError("");
    setGeneratingDetailLetter(true);
    try {
      const text = await disputeLettersApi.generateLetter(detailDisputeId, {
        letterType: letterGenLetterType,
        bureauTarget: letterGenBureau,
      });
      setGeneratedLetterBody(text);
    } catch {
      setDetailLetterError("Letter generation failed. Check the API and IntelliWarrior AI configuration.");
    } finally {
      setGeneratingDetailLetter(false);
    }
  };

  const handleSaveDetailLetterSent = async () => {
    if (!detailDraft || !detailDisputeId) {
      return;
    }
    if (!generatedLetterBody.trim()) {
      setDetailLetterError("Generate or enter letter text before saving.");
      return;
    }
    const templateName =
      disputeLetterOptions.find((t) => t.typeKey === letterGenLetterType)?.name ?? letterGenLetterType;
    setDetailLetterError("");
    setSavingDetailLetter(true);
    try {
      await disputeLettersApi.saveLetter(detailDisputeId, {
        letterContent: generatedLetterBody,
        letterType: templateName,
        bureauTarget: letterGenBureau,
        markAsSent: true,
      });
      const list = await disputeLettersApi.getLettersForDispute(detailDisputeId);
      setDetailLetters(list);
      await loadData();
      try {
        const fresh = await disputesApi.getById(detailDraft.clientId, detailDisputeId);
        setDetailDraft(fresh);
        if (selected) {
          setSelected({ ...fresh, clientName: selected.clientName });
        }
      } catch {
        /* ignore */
      }
    } catch {
      setDetailLetterError("Could not save letter.");
    } finally {
      setSavingDetailLetter(false);
    }
  };

  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      const bureaus = getBureaus(card);
      const bureauMatch = bureauFilter === "All" || bureaus.includes(bureauFilter);
      const statusMatch = statusFilter === "All" || normalizeColumn(card.status) === statusFilter.toLowerCase();
      return bureauMatch && statusMatch;
    });
  }, [cards, bureauFilter, statusFilter]);

  const grouped = useMemo(() => {
    return boardColumns.reduce<Record<BoardColumn, DisputeCard[]>>(
      (acc, col) => {
        acc[col.key] = filteredCards.filter((card) => normalizeColumn(card.status) === col.key);
        return acc;
      },
      {
        "in dispute": [],
        "awaiting response": [],
        updated: [],
        "deleted/resolved": [],
      }
    );
  }, [filteredCards]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Disputes Board</h1>
          <p className="text-sm text-slate-400">Track each dispute stage and review priority actions.</p>
        </div>

        <div className="flex flex-col gap-2 rounded-xl border border-slate-800 bg-slate-900 p-4 sm:flex-row sm:flex-wrap sm:gap-3">
          <select
            value={bureauFilter}
            onChange={(e) => setBureauFilter(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 sm:w-auto sm:min-w-[10rem]"
          >
            <option>All</option>
            <option>Equifax</option>
            <option>Experian</option>
            <option>TransUnion</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 sm:w-auto sm:min-w-[10rem]"
          >
            <option>All</option>
            <option>In Dispute</option>
            <option>Awaiting Response</option>
            <option>Updated</option>
            <option>Deleted/Resolved</option>
          </select>
        </div>

        {loading && (
          <div className="py-4" aria-busy="true" aria-label="Loading disputes">
            <TableSkeletonRows rows={7} cols={5} />
          </div>
        )}
        {!loading && error && <p className="text-sm text-red-300">{error}</p>}

        {!loading && !error && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            {boardColumns.map((column) => (
              <section key={column.key} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-white">{column.title}</h2>
                  <span className="rounded-full bg-slate-800 px-2 py-1 text-xs text-slate-300">
                    {grouped[column.key].length}
                  </span>
                </div>

                <div className="space-y-3">
                  {grouped[column.key].map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => openDetailModal(card)}
                      className={`w-full rounded-lg border p-3 text-left transition hover:border-blue-400/40 ${typeClass(
                        card.accountType
                      )}`}
                    >
                      <p className="text-sm font-semibold text-white">{card.creditor}</p>
                      <p className="text-xs text-slate-300">{card.clientName}</p>
                      <p className="mt-2 text-xs text-slate-400">Type: {card.accountType || "-"}</p>
                      <p className="text-xs text-slate-400">Bureaus: {getBureaus(card).join(", ") || "-"}</p>
                      <p className="text-xs text-slate-400">Letter: {card.letterType || "-"}</p>
                      <p className="text-xs text-slate-400">
                        Date Sent: {card.lastLetterSentAt ? new Date(card.lastLetterSentAt).toLocaleDateString() : "-"}
                      </p>
                      <p className="text-xs text-slate-400">Priority: #{card.priorityRank || 0}</p>
                    </button>
                  ))}

                  {grouped[column.key].length === 0 && (
                    <p className="rounded-lg border border-dashed border-slate-700 p-3 text-xs text-slate-500">
                      No disputes in this column.
                    </p>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {detailDisputeId && detailDraft && selected ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disputes-board-detail-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeDetailModal();
            }
          }}
        >
          <div
            className={`${modalCardClass} max-h-[90vh] w-full max-w-2xl overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="disputes-board-detail-title" className="text-lg font-semibold text-white">
              Dispute details
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              {detailDraft.creditor} · {selected.clientName}
            </p>

            <form className="mt-4 space-y-3" onSubmit={(e) => void submitDetailDisputeSave(e)}>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs text-slate-500 sm:col-span-2">
                  Creditor name
                  <input
                    className={modalInputClass}
                    value={detailDraft.creditor}
                    onChange={(e) =>
                      setDetailDraft((prev) => (prev ? { ...prev, creditor: e.target.value } : null))
                    }
                    autoComplete="organization"
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Account type
                  <select
                    className={modalInputClass}
                    value={detailDraft.accountType}
                    onChange={(e) =>
                      setDetailDraft((prev) => (prev ? { ...prev, accountType: e.target.value } : null))
                    }
                  >
                    {DISPUTE_ACCOUNT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Account number
                  <input
                    className={modalInputClass}
                    value={detailDraft.accountNumber}
                    onChange={(e) =>
                      setDetailDraft((prev) => (prev ? { ...prev, accountNumber: e.target.value } : null))
                    }
                    autoComplete="off"
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Balance
                  <input
                    type="number"
                    step="0.01"
                    className={modalInputClass}
                    value={detailDraft.balance}
                    onChange={(e) =>
                      setDetailDraft((prev) =>
                        prev ? { ...prev, balance: Number.parseFloat(e.target.value) || 0 } : null
                      )
                    }
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Status
                  <select
                    className={modalInputClass}
                    value={detailDraft.status}
                    onChange={(e) =>
                      setDetailDraft((prev) => (prev ? { ...prev, status: e.target.value } : null))
                    }
                  >
                    {DISPUTE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="block">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-xs text-slate-500">Dispute reason</span>
                    {(!detailDraft.creditor.trim() || !detailDraft.accountType) && (
                      <span
                        className="text-[11px] text-slate-500"
                        title="Fill in creditor name and account type first"
                      >
                        Fill in creditor name and account type first
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => void generateDisputeReasonWithAi()}
                    disabled={
                      generatingDisputeReason || !detailDraft.creditor.trim() || !detailDraft.accountType
                    }
                    title={
                      !detailDraft.creditor.trim() || !detailDraft.accountType
                        ? "Fill in creditor name and account type first"
                        : "Generate a draft dispute reason with IntelliWarrior AI (review before saving)"
                    }
                    className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600/20 px-3 py-1.5 text-xs text-emerald-400 transition-colors hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {generatingDisputeReason ? (
                      <>
                        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" aria-hidden>
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Generating…
                      </>
                    ) : (
                      <>
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        Generate with AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  className={`${modalInputClass} min-h-[100px] resize-y`}
                  rows={4}
                  value={detailDraft.disputeReason}
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, disputeReason: e.target.value } : null))
                  }
                  placeholder="Describe why this item should be disputed…"
                />
              </div>

              <fieldset className="rounded-lg border border-slate-800 p-3">
                <legend className="px-1 text-xs text-slate-500">Bureaus</legend>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={detailDraft.equifaxDisputed}
                      onChange={(e) =>
                        setDetailDraft((prev) =>
                          prev ? { ...prev, equifaxDisputed: e.target.checked } : null
                        )
                      }
                      className="rounded border-slate-600"
                    />
                    Equifax
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={detailDraft.experianDisputed}
                      onChange={(e) =>
                        setDetailDraft((prev) =>
                          prev ? { ...prev, experianDisputed: e.target.checked } : null
                        )
                      }
                      className="rounded border-slate-600"
                    />
                    Experian
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={detailDraft.transUnionDisputed}
                      onChange={(e) =>
                        setDetailDraft((prev) =>
                          prev ? { ...prev, transUnionDisputed: e.target.checked } : null
                        )
                      }
                      className="rounded border-slate-600"
                    />
                    TransUnion
                  </label>
                </div>
              </fieldset>

              <label className="block text-xs text-slate-500">
                Letter type
                <select
                  className={modalInputClass}
                  value={
                    disputeLetterOptions.some((t) => t.typeKey === detailDraft.letterType)
                      ? detailDraft.letterType
                      : disputeLetterOptions[0]?.typeKey ?? ""
                  }
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, letterType: e.target.value } : null))
                  }
                >
                  {disputeLetterOptions.map((t) => (
                    <option key={t.typeKey} value={t.typeKey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-xs text-slate-500">
                Notes
                <textarea
                  className={`${modalInputClass} min-h-[72px]`}
                  value={detailDraft.notes}
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, notes: e.target.value } : null))
                  }
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs text-slate-500">
                  Priority rank
                  <input
                    type="number"
                    step="1"
                    className={modalInputClass}
                    value={detailDraft.priorityRank}
                    onChange={(e) =>
                      setDetailDraft((prev) =>
                        prev ? { ...prev, priorityRank: Number.parseInt(e.target.value, 10) || 0 } : null
                      )
                    }
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Round number
                  <input
                    type="number"
                    step="1"
                    min={1}
                    className={modalInputClass}
                    value={detailDraft.roundNumber}
                    onChange={(e) =>
                      setDetailDraft((prev) =>
                        prev
                          ? { ...prev, roundNumber: Math.max(1, Number.parseInt(e.target.value, 10) || 1) }
                          : null
                      )
                    }
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Last letter sent
                  <input
                    type="datetime-local"
                    className={modalInputClass}
                    value={isoToDatetimeLocalValue(detailDraft.lastLetterSentAt)}
                    onChange={(e) =>
                      setDetailDraft((prev) =>
                        prev
                          ? { ...prev, lastLetterSentAt: datetimeLocalValueToIso(e.target.value) }
                          : null
                      )
                    }
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Response received
                  <input
                    type="datetime-local"
                    className={modalInputClass}
                    value={isoToDatetimeLocalValue(detailDraft.responseReceivedAt)}
                    onChange={(e) =>
                      setDetailDraft((prev) =>
                        prev
                          ? { ...prev, responseReceivedAt: datetimeLocalValueToIso(e.target.value) }
                          : null
                      )
                    }
                  />
                </label>
              </div>

              {detailSaveError && <p className="text-sm text-rose-300">{detailSaveError}</p>}

              <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
                <button
                  type="button"
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                  onClick={closeDetailModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={detailSaving}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {detailSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>

            <div className="mt-6 border-t border-slate-800 pt-4">
              <h4 className="text-sm font-semibold text-white">Generate letter</h4>
              <p className="mt-1 text-xs text-slate-500">
                Uses the same AI letter flow as the Dispute letters page.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-xs text-slate-500">
                  Letter type
                  <select
                    className={modalInputClass}
                    value={letterGenLetterType}
                    onChange={(e) => setLetterGenLetterType(e.target.value)}
                  >
                    {disputeLetterOptions.map((t) => (
                      <option key={t.typeKey} value={t.typeKey}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs text-slate-500">
                  Bureau target
                  <select
                    className={modalInputClass}
                    value={letterGenBureau}
                    onChange={(e) => setLetterGenBureau(e.target.value)}
                  >
                    {LETTER_BUREAU_TARGETS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="button"
                onClick={() => void handleGenerateDetailLetter()}
                disabled={generatingDetailLetter || !letterGenLetterType}
                className="mt-3 rounded-lg bg-emerald-600/20 px-4 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {generatingDetailLetter ? "Generating…" : "Generate Letter"}
              </button>
              <label className="mt-3 block text-xs text-slate-500">
                Letter content
                <textarea
                  className={`${modalInputClass} min-h-[160px] resize-y font-mono text-xs`}
                  value={generatedLetterBody}
                  onChange={(e) => setGeneratedLetterBody(e.target.value)}
                  placeholder="Generate a letter or paste text here…"
                />
              </label>
              <button
                type="button"
                onClick={() => void handleSaveDetailLetterSent()}
                disabled={savingDetailLetter || !generatedLetterBody.trim()}
                className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {savingDetailLetter ? "Saving…" : "Save & Mark as Sent"}
              </button>
              {detailLetterError && <p className="mt-2 text-sm text-rose-300">{detailLetterError}</p>}
            </div>

            <div className="mt-6 border-t border-slate-800 pt-4">
              <h4 className="text-sm font-semibold text-white">Letter history</h4>
              {detailLettersLoading ? (
                <p className="mt-2 text-sm text-slate-500">Loading letters…</p>
              ) : detailLetters.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No letters saved for this dispute yet.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {detailLetters.map((letter) => (
                    <li
                      key={letter.id}
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-300"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <span className="font-medium text-slate-200">{letter.letterType}</span>
                          <span className="mx-2 text-slate-600">·</span>
                          <span className="text-xs text-slate-500">
                            {new Date(letter.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedHistoryLetterId((prev) => (prev === letter.id ? null : letter.id))
                          }
                          className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                        >
                          {expandedHistoryLetterId === letter.id ? "Hide" : "View"}
                        </button>
                      </div>
                      {expandedHistoryLetterId === letter.id ? (
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-800 bg-slate-950 p-2 text-xs text-slate-300">
                          {letter.letterContent}
                        </pre>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
};

export default DisputesPage;
